import { useSetToast } from "@app/components/Toast";
import { useOptimistic } from "@app/shared/utils/optimisticUpdates";
import { Button } from "@design-system/Inputs/Button";
import { Box } from "@design-system/Layout/Box";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { cn } from "@design-system/Utilities";
import {
    ObjectiveTargetStatus,
    ResponseStatus,
    type GetObjectiveQuery,
} from "@generated/client/graphql";
import { useMemo, type ChangeEvent, type FC } from "react";
import { useTranslation } from "react-i18next";
import {
    useCreateObjectiveTarget,
    useDeleteObjectiveTarget,
    useUpdateObjectiveTarget,
} from "../../data";
import { isObjectiveEditable } from "../../services";
import { RESOURCE_NAME, keys } from "./i18n";
import { Numberfield } from "./Numberfield";
import { DateInput } from "./DateInput";
type TargetsProps = {
    objective: GetObjectiveQuery["objective"];
};

type TargetProps = {
    canBeDeleted?: boolean;
    editable?: boolean;
    onChange: (
        id: string | undefined,
        { value, date }: { value?: number; date?: Date },
    ) => void;
    onDelete: (id?: string) => void;
    showValue?: boolean;
    target?: GetObjectiveQuery["objective"]["targets"][number];
    unit?: string;
};

export const Targets: FC<TargetsProps> = ({ objective }) => {
    const { t } = useTranslation(RESOURCE_NAME);
    const { setToastError } = useSetToast();

    const canEditObjective = useMemo(
        () => isObjectiveEditable(objective),
        [objective],
    );

    const sortedTargets = useMemo(
        () =>
            objective.targets?.length
                ? objective.targets.sort(
                      (a, b) =>
                          new Date(a.date).getTime() -
                          new Date(b.date).getTime(),
                  )
                : /**
                   * In case there are no targets, we want to show an empty
                   * target to be able to add a new one. We do it here, or
                   * else we'd have to memoize the empty array too to avoid
                   * problems with the useEffect in the useOptimistic below.
                   */
                  [undefined],
        [objective.targets],
    );
    const [optimisticTargets, setOptimisticTargets] =
        useOptimistic<
            (GetObjectiveQuery["objective"]["targets"][number] | undefined)[]
        >(sortedTargets);

    const showAddTargetBtn = useMemo(() => {
        if (!canEditObjective) return false;
        /**
         * Always show it when there is an indicator.
         */
        if (objective.indicatorId) {
            return true;
        }

        /**
         * Show it when there are no targets and no indicator.
         */
        if (optimisticTargets.length === 0) {
            return true;
        }

        /**
         * Otherwise, if there is no indicator, but already one target,
         * we don't want to show the add target button.
         */
        return false;
    }, [objective.indicatorId, optimisticTargets.length, canEditObjective]);

    const { mutate: deleteTarget } = useDeleteObjectiveTarget(objective.id);
    const { mutate: updateTarget } = useUpdateObjectiveTarget();
    const { mutate: createTarget } = useCreateObjectiveTarget(objective.id);

    const handleAddTargetBtnClick = () => {
        setOptimisticTargets((prev) => [...prev, undefined]);
    };

    const handleDelete = (id?: string) => {
        if (!id) {
            return setOptimisticTargets((prev) =>
                prev.filter((_, index) => index !== prev.length - 1),
            );
        }

        setOptimisticTargets((prev) =>
            prev.filter((target) => target?.id !== id),
        );
        deleteTarget(id, {
            onSettled: (response) => {
                if (
                    response?.deleteObjectiveTarget?.status ===
                    ResponseStatus.Error
                ) {
                    setToastError(
                        `${response.deleteObjectiveTarget.error?.message}`,
                    );
                }
            },
            onError: () => {
                setToastError(keys.update_error);
            },
        });
    };

    const handleChange = (
        id: string | undefined,
        { value, date }: { value?: number; date?: Date },
    ) => {
        const opts = {
            onError: () => {
                setToastError(t(keys.update_error));
            },
        };

        if (!id) {
            return createTarget(
                {
                    value: {
                        number: value,
                    },
                    date,
                    status: ObjectiveTargetStatus.Ongoing,
                    objectiveId: objective.id,
                },
                opts,
            );
        }

        updateTarget(
            {
                id,
                patch: {
                    value: {
                        number: value,
                    },
                    date,
                },
            },
            opts,
        );
    };

    return (
        <FlexCol gap="4">
            {optimisticTargets.map((target, index) => {
                return (
                    <Target
                        key={target?.id ?? index}
                        editable={canEditObjective}
                        canBeDeleted={
                            optimisticTargets.length > 1 && canEditObjective
                        }
                        target={target}
                        onChange={handleChange}
                        onDelete={handleDelete}
                        unit={objective.indicator?.unit?.shortName ?? undefined}
                        showValue={!!objective.indicatorId}
                    />
                );
            })}
            {showAddTargetBtn && (
                <Button
                    className="w-fit"
                    variant="textLink"
                    onClick={handleAddTargetBtnClick}
                >
                    <Button.Icon name="plus" />
                    {t(keys.add_target)}
                </Button>
            )}
        </FlexCol>
    );
};

const Target: FC<TargetProps> = ({
    canBeDeleted = true,
    editable = true,
    onChange,
    onDelete,
    showValue,
    target,
    unit,
}) => {
    const { t } = useTranslation(RESOURCE_NAME);

    const memoizedDate = useMemo(() => {
        return target?.date ? new Date(target.date) : undefined;
    }, [target?.date]);

    const [value, setValue] = useOptimistic(target?.value?.number);
    const [date, setDate] = useOptimistic(memoizedDate);

    const handleValueChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!editable) return;
        setValue(e.target.value);
    };

    const handleValueBlur = () => {
        if (!editable) return;
        const numberValue = Number(value);
        /**
         * To create a new target, we need to have both value and year.
         * We don't want to update the target if the value is the same
         * as the target's value (meaning the user didn't change the value).
         */
        if (!date || numberValue === target?.value?.number) return;
        onChange(target?.id, { value: numberValue, date });
    };

    const handleDateChange = (date: Date | undefined) => {
        if (!editable) return;
        /**
         * We do not want to unselect the year.
         */
        if (date == undefined) return;

        setDate(date);

        /**
         * To create a new target, we need to have both value and year.
         * Unless there is no indicator, in which case we can create a
         * target with a date only (no value).
         */
        if (!showValue) {
            return onChange(target?.id, { date });
        }

        if (value == undefined) return;
        const numberValue = Number(value);
        onChange(target?.id, { value: numberValue, date });
    };

    const handleDelete = () => {
        if (!editable) return;
        onDelete(target?.id);
    };

    return (
        <FlexRow className="" alignItems="center" gap="1">
            <FlexRow gap="4">
                {showValue && (
                    <Box
                        className={cn(
                            "w-[160px]",
                            !editable &&
                                "pointer-events-none cursor-not-allowed",
                        )}
                    >
                        <Numberfield
                            align="right"
                            hideControls
                            onBlur={handleValueBlur}
                            onChange={handleValueChange}
                            placeholder={t(keys.target_value)}
                            required
                            unit={unit}
                            value={value}
                        />
                    </Box>
                )}
                <DateInput
                    editable={editable}
                    value={date}
                    onChange={handleDateChange}
                />
            </FlexRow>
            {!!canBeDeleted && (
                <Button variant="text" onClick={handleDelete} size="sm">
                    <Button.Icon name="trash" />
                </Button>
            )}
        </FlexRow>
    );
};
